import { ErrorMessage, useFormikContext } from 'formik';
import { useMemo } from 'react';

import { Checkbox } from '@src/design-system/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@src/design-system/command';
import { CustomErrorMessage } from '@src/design-system/error-message';
import { cn } from '@src/design-system/helpers';
import { inputVariants } from '@src/design-system/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@src/design-system/popover';

type RuleChoicesFormValues = {
  rule_choices?: string[];
};

export type TRuleChoiceOption = {
  label: string;
  value: string;
};

type RuleChoicesMultiSelectProps = {
  options: TRuleChoiceOption[];
  placeholder?: string;
};

export function RuleChoicesMultiSelect({
  options,
  placeholder = 'Select',
}: RuleChoicesMultiSelectProps) {
  const { values, setFieldValue, setFieldTouched, errors, touched } =
    useFormikContext<RuleChoicesFormValues>();

  const selectedChoices = useMemo(
    () => new Set(values.rule_choices ?? []),
    [values.rule_choices]
  );

  const updateSelection = (choice: string, checked: boolean) => {
    const next = new Set(selectedChoices);

    if (checked) next.add(choice);
    else next.delete(choice);

    setFieldValue(
      'rule_choices',
      options.filter((option) => next.has(option.value)).map((o) => o.value)
    );
    setFieldTouched('rule_choices', true, false);
  };

  const selectedLabel = options
    .filter((option) => selectedChoices.has(option.value))
    .map((option) => option.label)
    .join(', ');

  return (
    <div className='min-w-0'>
      <Popover modal>
        <PopoverTrigger
          type='button'
          {...(errors.rule_choices &&
            touched.rule_choices && { 'data-error': 'error' })}
          className={cn(
            inputVariants(),
            'flex w-full cursor-pointer items-center text-sm',
            !selectedLabel && 'text-gray-500'
          )}
        >
          <span className='truncate'>{selectedLabel || placeholder}</span>
        </PopoverTrigger>
        <PopoverContent
          align='start'
          className='w-[var(--radix-popover-trigger-width)] min-w-[200px] p-0'
        >
          <Command>
            <CommandList className='max-h-[min(240px,var(--radix-popover-content-available-height,240px))]'>
              <CommandEmpty>No choices found</CommandEmpty>
              <CommandGroup className='p-0'>
                {options.map(({ label, value }) => {
                  const isSelected = selectedChoices.has(value);

                  return (
                    <CommandItem
                      key={value}
                      value={label}
                      className='py-2 block overflow-hidden bg-white last:mb-0 border-b border-primary-200 last:border-b-0 rounded-none'
                      data-selected={false}
                      onSelect={() => updateSelection(value, !isSelected)}
                    >
                      <div className='flex items-center gap-2'>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) => {
                            updateSelection(value, Boolean(checked));
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <p className='text-sm text-gray-900'>{label}</p>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <ErrorMessage
        name='rule_choices'
        component={CustomErrorMessage}
      />
    </div>
  );
}
